import React from "react";
import { useState, useEffect, useCallback } from "react";
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import {
  getParametersData,
  setParametersData,
} from "../../../services/user.service";
import { customSort } from "../../../functions/sort.function";
import { useLocation, useParams } from "react-router-dom";
import Button from "../../../components/button";
import styles from "./styles.module.css";

registerLocale("es", es);

function getThresholdRange(thresholds, i) {
  const lower = thresholds[`threshold_level${i}_lower`];
  const upper = thresholds[`threshold_level${i}_upper`];
  return `[${lower}, ${upper}]`;
}

function getThresholdString(thresholds) {
  const thresholdStrs = [];

  for (let i = 1; i <= Object.keys(thresholds).length / 2; i++) {
    thresholdStrs.push(`Umbral ${i} ${getThresholdRange(thresholds, i)}`);
  }

  return thresholdStrs.join(", ");
}

function parByProd(data, parpage) {
  if (parpage === "lastprice") {
    return data.reduce((map, obj) => {
      const prod = obj.product;

      if (!map[prod]) {
        map[prod] = { size: new Map(), spread: new Map() };
      }

      map[prod].size.set(obj.dtime, obj.minsize_q25);
      map[prod].spread.set(obj.dtime, obj.maxspread_q75);

      return map;
    }, {});
  }
}

function curParByProd(data) {
  return data.reduce((map, obj) => {
    const prod = obj.product;

    if (!map[prod]) {
      map[prod] = new Map();
    }
    map[prod].set(obj.mibparamtype, obj.mibparamdate);
    return map;
  }, {});
}

function Parameters() {
  const [data, setData] = useState(new Map());
  const [currentParams, setCurrentParams] = useState(new Map());
  const [paramSelection, setParamsSelection] = useState(new Map());
  let { parpage } = useParams();
  let location = useLocation();

  const refresh = useCallback(() => {
    getParametersData(parpage)
      .then((res) => {
        let dbdata = JSON.parse(res.data);
        let organizedparams = parByProd(
          dbdata.params.sort((a, b) => customSort(a.product, b.product)),
          parpage
        );
        let currparams = curParByProd(dbdata.currentparams);
        setData(organizedparams);
        setCurrentParams(currparams);
        setParamsSelection(curParByProd(dbdata.currentparams));
      })
      .catch((err) => console.log(err));
  }, [parpage]);

  useEffect(() => {
    refresh();
  }, [location, parpage, refresh]);

  const Dropmedown = (onChange, product, partype) => {
    const datapartype = partype.split(":")[1];
    let options2 = new Map();
    if (partype === "fastmarket:threshold") {
      data?.[product]?.[datapartype]?.forEach((vv, pardate) => {
        options2.set(pardate, getThresholdString(vv));
      });
    } else if (partype === "fastmarket:spread") {
      data?.[product]?.[datapartype]?.forEach((vv, pardate) => {
        options2.set(
          pardate,
          `Spread [${vv.maxspread_q75}, ${vv.maxspread_q90}]`
        );
      });
    } else {
      options2 = data?.[product]?.[datapartype];
      if (
        options2 &&
        partype === "lastprice:spread" &&
        typeof [...options2?.entries()] === "object" &&
        [...options2?.entries()][0][1]?.hasOwnProperty("maxspread_q90")
      ) {
        options2 = undefined;
      }
    }
    if (options2) {
      let msdfs =
        data?.[product]?.[datapartype]?.has(
          paramSelection?.[product]?.get(partype)
        ) ?? false;
      let paramdate = paramSelection?.[product]?.get(partype) ?? "";
      return (
        <div>
          <select
            className={styles.dropdown}
            value={paramdate}
            onChange={(e) => onChange(e, product, partype)}
          >
            {msdfs ? (
              ""
            ) : (
              <option value="" selected disabled hidden>
                Set
              </option>
            )}
            {[...options2?.entries()].map(([key, val]) => (
              <option value={key}>
                {key}: {val}
              </option>
            ))}
          </select>
        </div>
      );
    }
  };

  const handleDropdownChange = (event, product, partype) => {
    let nuparsel = { ...paramSelection };
    if (!nuparsel[product]) {
      nuparsel[product] = new Map();
    }
    nuparsel[product].set(partype, event.target.value);
    setParamsSelection(nuparsel);
  };

  const compareArrays = (original, currentData) => {
    const result = [];
    Object.keys(currentData).forEach((product) => {
      let prodData = currentData[product];
      prodData.forEach((vv, partype) => {
        if (vv !== original?.[product]?.get(partype)) {
          const updaterobj = {
            product: product,
            mibparamtype: partype,
            mibparamdate: vv,
          };
          result.push(updaterobj);
        }
      });
    });
    return result;
  };
  const getParColumnsVals = (product, partype) => {
    const datapartype = partype.split(":")[1];
    const whatever = data?.[product]?.[datapartype]?.get(
      paramSelection?.[product]?.get(partype)
    );

    let cols = [];
    if (!whatever) {
      if (partype === "fastmarket:threshold") {
        cols = Array.from({ length: 4 }, (_, i) => (
          <td key={`${product}${partype}${i}`} />
        ));
      } else {
        cols = [<td key={`${product}${partype}`} />];
      }
    } else {
      if (partype === "fastmarket:threshold") {
        cols = Array.from({ length: 4 }, (_, i) => (
          <td className={styles.tableCell} key={`${product}${partype}${i}`}>
            {" "}
            {getThresholdRange(whatever, i + 1)}
          </td>
        ));
      } else if (partype === "fastmarket:spread") {
        cols = [
          <td className={styles.tableCell} key={`${product}${partype}`}>
            {" "}
            {`[${whatever.maxspread_q75}, ${whatever.maxspread_q90}]`}{" "}
          </td>,
        ];
      } else {
        cols = [
          <td className={styles.tableCell} key={`${product}${partype}`}>
            {" "}
            {whatever}{" "}
          </td>,
        ];
      }
    }
    return <>{cols}</>;
  };

  const handleClick = () => {
    let valueUpdater = compareArrays(currentParams, paramSelection);
    setParametersData(valueUpdater)
      .then((response) => {
        refresh();
      })
      .catch((error) => {
        console.log(error);
        refresh();
      });
  };

  const parpage2title = { lastprice: "Last Price" };

  const parpage2cols = {
    lastprice: [
      "Producto",
      "Fecha Cantidad",
      "Cantidad Q25",
      "Fecha Spread",
      "Spread Q75",
    ],
  };

  const parpage2types = {
    lastprice: { "lastprice:size": "size", "lastprice:spread": "spread" },
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          Parámetros {`${parpage2title[parpage]}`}
        </h3>
        <Button variant="outline" onClick={() => handleClick()}>
          Actualizar Parámetros
        </Button>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.tableHeader}>
              {parpage2cols[parpage].map((key) => (
                <th className={styles.tableHeaderCell} key={key}>
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => (
              <tr
                key={"parammrow" + key}
                className={
                  index % 2 === 1 ? styles.tableRowAlt : styles.tableRow
                }
              >
                <td className={styles.tableCell}>{key}</td>
                {Object.entries(parpage2types[parpage]).map(([it, iit]) => (
                  <>
                    <td className={styles.tableCell}>
                      {" "}
                      {Dropmedown(handleDropdownChange, key, it)}
                    </td>
                    {getParColumnsVals(key, it)}
                  </>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Parameters;
